import type { ReactNode } from 'react';
import { Link } from 'react-router';

import type { AnchorId } from '@/guidance/anchors';
import { cn } from '@/shared/utils/cn';

import { Icon, type IconName } from './Icon';

export interface ModuleNavigationItem {
  to: string;
  label: string;
  code?: string;
  icon?: IconName;
  current?: 'page' | 'location';
  accessibleLabel?: string;
  trailing?: ReactNode;
  tour?: AnchorId;
  /**
   * C2, Q5: a record's own sections pass on where the record was opened from (its location state, so
   * BackLink keeps returning to that view of the list) and replace the history entry, so Back leaves
   * the record instead of stepping through its sections. Absent, the link pushes with no state.
   */
  state?: unknown;
  replace?: boolean;
}

export interface ModuleNavigationGroup {
  id: string;
  label?: string;
  items: ModuleNavigationItem[];
}

/** Shared contextual navigation: authorised destinations supplied by the caller. */
export function ModuleNavigation({
  id,
  moduleId,
  label,
  title,
  groups,
  onNavigate,
  tone = 'default',
  responsive = false,
  tourId,
  status,
}: {
  id?: string;
  moduleId: string;
  label: string;
  title: string;
  groups: ModuleNavigationGroup[];
  onNavigate?: () => void;
  tone?: 'default' | 'shell';
  responsive?: boolean;
  tourId?: AnchorId;
  status?: string;
}) {
  const shell = tone === 'shell';

  return (
    <nav id={id} aria-label={label} data-module-context={moduleId} data-tour={tourId}>
      <p
        className={cn(
          'type-ui mb-3 px-2 font-semibold wrap-break-word',
          shell ? 'text-ink-on-shell' : 'text-ink-heading',
        )}
      >
        {title}
      </p>
      <div className="space-y-4">
        {groups
          .filter((group) => group.items.length > 0)
          .map((group) => (
            <div key={group.id}>
              {group.label ? (
                <p
                  className={cn(
                    'type-label px-2 pb-1',
                    shell ? 'text-ink-on-shell-muted' : 'text-ink-muted',
                  )}
                >
                  {group.label}
                </p>
              ) : null}
              <ul
                className={cn(
                  'space-y-1',
                  responsive && 'lg:flex lg:flex-wrap lg:gap-1 lg:space-y-0 xl:block xl:space-y-1',
                )}
              >
                {group.items.map((item) => (
                  <li key={item.to} className={responsive ? 'min-w-0 lg:max-w-full' : undefined}>
                    <Link
                      to={item.to}
                      state={item.state}
                      replace={item.replace}
                      aria-current={item.current}
                      aria-label={item.accessibleLabel}
                      data-module-destination={item.to}
                      data-tour={item.tour}
                      onClick={onNavigate}
                      className={cn(
                        'flex min-h-11 items-center gap-2 rounded-md border-s-2 px-2 py-2 text-start focus-visible:outline-2 focus-visible:outline-focus',
                        shell
                          ? item.current
                            ? 'border-focus bg-shell-active text-ink-on-brand'
                            : 'border-transparent text-ink-on-shell-muted hover:bg-shell-hover hover:text-ink-on-shell'
                          : item.current
                            ? 'border-primary bg-selected text-ink-heading'
                            : 'border-transparent text-ink hover:bg-hover',
                      )}
                    >
                      {item.icon ? <Icon name={item.icon} className="shrink-0" /> : null}
                      <span className="min-w-0 flex-1 wrap-break-word">
                        <span className="type-ui font-medium">{item.label}</span>
                        {item.code ? (
                          <bdi
                            dir="ltr"
                            className={cn(
                              'type-meta numeric block',
                              shell ? 'text-ink-on-shell-muted' : 'text-ink-muted',
                            )}
                          >
                            {item.code}
                          </bdi>
                        ) : null}
                      </span>
                      {item.trailing}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
      </div>
      {status ? (
        <p
          role="status"
          className={cn(
            'type-meta mt-3 px-2',
            shell ? 'text-ink-on-shell-muted' : 'text-ink-muted',
          )}
        >
          {status}
        </p>
      ) : null}
    </nav>
  );
}
